import { apiClient, apiClientLong } from './client';
import type {
  AllPantsuResponse,
  AllocateStatResult,
  AllocateTraitResult,
  ArmorTiersResponse,
  AutobuySettings,
  AutobuySettingsUpdate,
  AutouseResult,
  BuyItemRequest,
  BuyItemResult,
  ConsumablesResponse,
  CreateCharacterRequest,
  CreateCharacterResponse,
  EquipMimicRequest,
  EquipPantsuRequest,
  EquipResult,
  EventsArchiveResponse,
  ExploredLocationsResponse,
  InventoryResponse,
  ItemCountResponse,
  ItemsResponse,
  LearnSkillRequest,
  LearnSkillResult,
  LevelTableResponse,
  MapLocationsResponse,
  MapPositionsResponse,
  PantsuInventoryResponse,
  PlayerResponse,
  PlayerSettings,
  PlayerSkillsResponse,
  RecentEventsResponse,
  RelationshipsResponse,
  ShopCheckResponse,
  ShopResponse,
  SkillsResponse,
  SuccessResponse,
  TravelTargetResponse,
  UseItemRequest,
  UseItemResult,
  Waifu,
  WaifuEquipmentResponse,
  WaifuListResponse,
  WaifuReferenceData,
  WeaponTiersResponse,
} from '@/types';

// ============ Player ============

export const playerApi = {
  get: (userId: number) => apiClient.get<PlayerResponse>(`/player/${userId}`).then((r) => r.data),
  create: (body: CreateCharacterRequest) =>
    apiClientLong.post<CreateCharacterResponse>('/player/create', body).then((r) => r.data),
  updateSettings: (userId: number, body: PlayerSettings) =>
    apiClient.post<SuccessResponse>(`/player/${userId}/settings`, body).then((r) => r.data),
  getPosition: (userId: number) =>
    apiClient.get(`/player/${userId}/position`).then((r) => r.data),
  setTravelTarget: (userId: number, locationId: string) =>
    apiClient
      .post<TravelTargetResponse>(`/player/${userId}/travel-target`, { location_id: locationId })
      .then((r) => r.data),
  clearTravelTarget: (userId: number) =>
    apiClient.delete<SuccessResponse>(`/player/${userId}/travel-target`).then((r) => r.data),
};

// ============ Skills ============

export const skillsApi = {
  getAll: () => apiClient.get<SkillsResponse>('/skills').then((r) => r.data),
  getPlayer: (userId: number) =>
    apiClient.get<PlayerSkillsResponse>(`/player/${userId}/skills`).then((r) => r.data),
  learn: (userId: number, body: LearnSkillRequest) =>
    apiClient.post<LearnSkillResult>(`/player/${userId}/skills/learn`, body).then((r) => r.data),
};

// ============ Waifus ============

export const waifuApi = {
  list: (userId: number) =>
    apiClient.get<WaifuListResponse>(`/waifus/${userId}`).then((r) => r.data),
  get: (waifuId: number) => apiClient.get<Waifu>(`/waifu/${waifuId}`).then((r) => r.data),
  reference: () =>
    apiClient.get<WaifuReferenceData>('/reference/waifu').then((r) => r.data),
  allocateStat: (waifuId: number, statName: string) =>
    apiClient
      .post<AllocateStatResult>(`/waifu/${waifuId}/allocate-stat`, { stat_name: statName })
      .then((r) => r.data),
  allocateTrait: (waifuId: number, traitId: string) =>
    apiClient
      .post<AllocateTraitResult>(`/waifu/${waifuId}/allocate-trait`, { trait_id: traitId })
      .then((r) => r.data),
};

// ============ Relationships / Level Table / Explored ============

export const miscApi = {
  relationships: (userId: number) =>
    apiClient.get<RelationshipsResponse>(`/relationships/${userId}`).then((r) => r.data),
  levelTable: () =>
    apiClient.get<LevelTableResponse>('/level-table').then((r) => r.data),
  weaponTiers: () =>
    apiClient.get<WeaponTiersResponse>('/weapon-tiers').then((r) => r.data),
  armorTiers: () =>
    apiClient.get<ArmorTiersResponse>('/armor-tiers').then((r) => r.data),
  items: () => apiClient.get<ItemsResponse>('/items').then((r) => r.data),
  explored: (userId: number) =>
    apiClient
      .get<ExploredLocationsResponse>(`/explored/${userId}`)
      .then((r) => r.data),
  health: () =>
    apiClient.get<{ status: string; message: string }>('/health').then((r) => r.data),
};

// ============ Map / Locations ============

export const mapApi = {
  locations: () =>
    apiClient.get<MapLocationsResponse>('/map/locations').then((r) => r.data),
  positions: () =>
    apiClient.get<MapPositionsResponse>('/map/positions').then((r) => r.data),
  allLocations: () =>
    apiClient
      .get<{ locations: unknown[]; travel_time_seconds: number }>('/locations')
      .then((r) => r.data),
  location: (id: string) => apiClient.get(`/locations/${id}`).then((r) => r.data),
};

// ============ Events ============

export const eventsApi = {
  recent: (userId: number, limit = 10) =>
    apiClient
      .get<RecentEventsResponse>(`/events/${userId}/recent`, { params: { limit } })
      .then((r) => r.data),
  archive: (userId: number, page = 1, perPage = 20) =>
    apiClient
      .get<EventsArchiveResponse>(`/events/${userId}/archive`, {
        params: { page, per_page: perPage },
      })
      .then((r) => r.data),
};

// ============ Items / Inventory ============

export const inventoryApi = {
  all: () => apiClient.get('/items').then((r) => r.data),
  get: (userId: number) =>
    apiClient.get<InventoryResponse>(`/inventory/${userId}`).then((r) => r.data),
  use: (userId: number, body: UseItemRequest) =>
    apiClient.post<UseItemResult>(`/inventory/${userId}/use`, body).then((r) => r.data),
  waifusForItem: (userId: number, itemId: string) =>
    apiClient
      .get<WaifuListResponse>(`/inventory/${userId}/waifus-for-item/${itemId}`)
      .then((r) => r.data),
  itemCount: (userId: number, itemId: string) =>
    apiClient
      .get<ItemCountResponse>(`/inventory/${userId}/item/${itemId}/count`)
      .then((r) => r.data),
  addItem: (userId: number, itemId: string, quantity = 1) =>
    apiClient
      .post<SuccessResponse>(`/inventory/${userId}/add`, null, {
        params: { item_id: itemId, quantity },
      })
      .then((r) => r.data),
};

// ============ Shop ============

export const shopApi = {
  get: (userId: number) =>
    apiClient.get<ShopResponse>(`/shop/${userId}`).then((r) => r.data),
  check: (userId: number) =>
    apiClient.get<ShopCheckResponse>(`/shop/${userId}/check`).then((r) => r.data),
  buy: (userId: number, body: BuyItemRequest) =>
    apiClient.post<BuyItemResult>(`/shop/${userId}/buy`, body).then((r) => r.data),
  consumables: () =>
    apiClient.get<ConsumablesResponse>('/shop/consumables').then((r) => r.data),
};

// ============ Autobuy / Autouse ============

export const autobuyApi = {
  get: (userId: number) =>
    apiClient.get<AutobuySettings>(`/player/${userId}/autobuy`).then((r) => r.data),
  update: (userId: number, body: AutobuySettingsUpdate) =>
    apiClient.post<SuccessResponse>(`/player/${userId}/autobuy`, body).then((r) => r.data),
  trigger: (userId: number) =>
    apiClient.post<AutouseResult>(`/player/${userId}/autouse`).then((r) => r.data),
};

// ============ Equipment / Pantsu / Ring ============

export const equipmentApi = {
  get: (waifuId: number, userId?: number) =>
    apiClient
      .get<WaifuEquipmentResponse>(`/waifu/${waifuId}/equipment`, {
        params: userId !== undefined ? { user_id: userId } : undefined,
      })
      .then((r) => r.data),
  upgradeWeapon: (waifuId: number, userId: number) =>
    apiClient
      .post<EquipResult>(`/waifu/${waifuId}/upgrade-weapon`, null, { params: { user_id: userId } })
      .then((r) => r.data),
  upgradeArmor: (waifuId: number, userId: number) =>
    apiClient
      .post<EquipResult>(`/waifu/${waifuId}/upgrade-armor`, null, { params: { user_id: userId } })
      .then((r) => r.data),
  equipPantsu: (waifuId: number, userId: number, body: EquipPantsuRequest) =>
    apiClient
      .post<EquipResult>(`/waifu/${waifuId}/equip-pantsu`, body, { params: { user_id: userId } })
      .then((r) => r.data),
  equipMimic: (waifuId: number, userId: number, body: EquipMimicRequest) =>
    apiClient
      .post<EquipResult>(`/waifu/${waifuId}/equip-mimic`, body, { params: { user_id: userId } })
      .then((r) => r.data),
  unequipPantsu: (waifuId: number, userId: number) =>
    apiClient
      .post<EquipResult>(`/waifu/${waifuId}/unequip-pantsu`, null, { params: { user_id: userId } })
      .then((r) => r.data),
  equipRing: (waifuId: number, userId: number) =>
    apiClient
      .post<EquipResult>(`/waifu/${waifuId}/equip-ring`, null, { params: { user_id: userId } })
      .then((r) => r.data),
  playerPantsu: (userId: number) =>
    apiClient
      .get<PantsuInventoryResponse>(`/player/${userId}/pantsu`)
      .then((r) => r.data),
  allPantsu: () =>
    apiClient.get<AllPantsuResponse>('/pantsu/all').then((r) => r.data),
};
